// src/pages/finance-portal/reports/StatementOfEquity.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Alert, Grid,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    CircularProgress, Stack,
} from '@mui/material';
import PieChartIcon from '@mui/icons-material/PieChart';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import axiosInstance from '../../../utils/axiosInstance';
import {
    fontFamily, ACCENT, formatCurrency,
    firstOfYearIso, todayIso, downloadCsv, printReport, AccountLink,
} from './reportShared';

export default function StatementOfEquity() {
    const [start, setStart] = useState(firstOfYearIso());
    const [end, setEnd] = useState(todayIso());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const res = await axiosInstance.get('/api/finance/reports/statement-of-equity', {
                params: { start_date: start, end_date: end },
            });
            if (res.data && res.data.success) {
                setData({
                    ...res.data,
                    accounts: Array.isArray(res.data.accounts) ? res.data.accounts : [],
                });
            } else {
                setError(res.data?.message || 'Failed to load');
            }
        } catch (e) {
            console.error('Statement of Equity load error:', e);
            setError(e.response?.data?.message || e.message || 'Failed to load statement of equity');
        } finally { setLoading(false); }
    }, [start, end]);

    useEffect(() => { load(); }, [load]);

    const exportCsv = () => {
        if (!data) return;
        downloadCsv(`statement_of_equity_${start}_${end}.csv`, data.accounts,
            ['account_number', 'account_name', 'opening_balance', 'net_change', 'closing_balance']);
    };

    return (
        <Box sx={{ p: 3, fontFamily }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <PieChartIcon sx={{ color: ACCENT }} />
                <Typography variant="h5" sx={{ fontFamily, fontWeight: 700 }}>Statement of Equity</Typography>
            </Stack>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={5}>
                        <TextField label="Start Date" type="date" fullWidth size="small"
                            InputLabelProps={{ shrink: true }}
                            value={start} onChange={(e) => setStart(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <TextField label="End Date" type="date" fullWidth size="small"
                            InputLabelProps={{ shrink: true }}
                            value={end} onChange={(e) => setEnd(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={2} sx={{ textAlign: 'right' }}>
                        <Button onClick={load} variant="contained"
                            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#15803d' } }}>Run</Button>
                    </Grid>
                </Grid>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

            {data && !loading && (
                <Paper sx={{ p: 3 }} id="se-report">
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                        <Box>
                            <Typography variant="h6">Statement of Equity</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {data.period?.start_date} – {data.period?.end_date}
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                            <Button size="small" startIcon={<PrintIcon />} onClick={() => printReport('se-report')}>Print</Button>
                            <Button size="small" startIcon={<DownloadIcon />} onClick={exportCsv}>CSV</Button>
                        </Stack>
                    </Stack>

                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Account</TableCell>
                                    <TableCell align="right">Opening</TableCell>
                                    <TableCell align="right">Net Change</TableCell>
                                    <TableCell align="right">Closing</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(data.accounts || []).map((r) => (
                                    <TableRow key={r.account_id}>
                                        <TableCell>
                                            <AccountLink
                                                accountId={r.account_id}
                                                label={`${r.account_number} — ${r.account_name}`}
                                                startDate={start}
                                                endDate={end}
                                            />
                                        </TableCell>
                                        <TableCell align="right">{formatCurrency(r.opening_balance)}</TableCell>
                                        <TableCell align="right">{formatCurrency(r.net_change)}</TableCell>
                                        <TableCell align="right">{formatCurrency(r.closing_balance)}</TableCell>
                                    </TableRow>
                                ))}
                                <TableRow>
                                    <TableCell>Current period net income</TableCell>
                                    <TableCell align="right">—</TableCell>
                                    <TableCell align="right">{formatCurrency(data.net_income)}</TableCell>
                                    <TableCell align="right">—</TableCell>
                                </TableRow>
                                <TableRow sx={{ '& td': { borderTop: '3px double #111827', fontWeight: 700 } }}>
                                    <TableCell>Total Equity</TableCell>
                                    <TableCell align="right">{formatCurrency(data.opening_total)}</TableCell>
                                    <TableCell align="right">—</TableCell>
                                    <TableCell align="right">{formatCurrency(data.closing_total)}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}
        </Box>
    );
}
